<template>
    <c-modal
        :value="value"
        :title="`${providerLabel} Rate Limit`"
        size="lg"
        hide-footer
        @input="$emit('input', $event)"
    >
        <b-alert show variant="secondary" class="d-flex align-items-center gap-2 mb-4 rate-limit-alert">
            <div class="info-icon">
                <font-awesome-icon icon="info" />
            </div>
            <span class="ml-2">
                Set a usage limit for {{ providerLabel }} requests to prevent abuse and control API costs. You can
                define how many attempts are allowed per IP and how long users must wait before trying again.
            </span>
        </b-alert>

        <b-form-group label="Attempts per IP">
            <b-form-input v-model.number="form.attemptsPerIp" type="number" min="0" :number="true" />
            <b-form-text
                >Maximum number of requests allowed from the same IP. Leave empty or 0 to disable rate limiting for this
                provider.</b-form-text
            >
        </b-form-group>

        <b-form-group label="Retry After (minutes)">
            <b-form-input v-model.number="form.retryAfterMinutes" type="number" min="0" :number="true" />
            <b-form-text
                >How long users must wait to make request once the limit is reached. Leave empty or 0 to use the default
                (10 minutes).</b-form-text
            >
        </b-form-group>

        <div class="d-flex justify-content-between mt-3">
            <b-btn variant="outline-dark" @click="$emit('input', false)">Close</b-btn>
            <loader-btn variant="primary" :loading="saving" @click="onSave">Save</loader-btn>
        </div>
    </c-modal>
</template>

<script lang="ts" setup>
import { reactive, ref, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import CModal from '@/components/CModal.vue';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import { saveRateLimit } from '@/api/user';

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'saved'): void;
}>();

const props = defineProps({
    value: { type: Boolean, required: true as const },
    provider: { type: String, required: true as const },
    providerLabel: { type: String, required: true as const },
    attemptsPerIp: { default: null as number | null },
    retryAfterMinutes: { default: null as number | null }
});

const saving = ref(false);

const form = reactive({
    attemptsPerIp: null as number | null,
    retryAfterMinutes: null as number | null
});

watch(
    () => props.value,
    (open) => {
        if (open) {
            form.attemptsPerIp = props.attemptsPerIp;
            form.retryAfterMinutes = props.retryAfterMinutes;
        }
    }
);

const onSave = async () => {
    saving.value = true;
    try {
        await saveRateLimit({
            provider: props.provider,
            attemptsPerIp: form.attemptsPerIp || null,
            retryAfterMinutes: form.retryAfterMinutes || null
        });
        emit('saved');
        emit('input', false);
    } finally {
        saving.value = false;
    }
};
</script>

<style lang="scss" scoped>
.rate-limit-alert {
    background-color: #ebebeb !important;
    color: #000 !important;
    border: none !important;
    border-radius: 8px !important;
    font-size: 16px;

    span {
        line-height: 1.5;
    }
}

.info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    min-width: 20px;
    border-radius: 50%;
    background-color: #333;
    color: white;
    font-size: 12px;
    margin-right: 12px;
    flex-shrink: 0;
}
</style>
